// Road-trim geometry: projecting a drag onto a road's polyline, slicing off
// the excluded head/tail, and locating junction snaps with the other roads
// in the route. Pure helpers — no React, no data fetching.

export interface RoadTrim {
  // Cut where the ride STARTS on this road — everything before it is excluded.
  head?: { pos: number; coord: [number, number] } | null
  // Cut where the ride ENDS on this road — everything after it is excluded.
  tail?: { pos: number; coord: [number, number] } | null
  // True when the ride travels this road backwards (tail → head). The cuts
  // stay positional; only travel direction and the Start/End labels flip.
  reversed?: boolean
}

// All trims in the current route, keyed by road id.
export type RouteTrims = Record<string, RoadTrim>

export type Coord = [number, number]

const EARTH_M = 6371000
const toRad = (d: number) => (d * Math.PI) / 180

export function metersBetween(a: Coord, b: Coord): number {
  const dLat = toRad(b[1] - a[1])
  const dLng = toRad(b[0] - a[0])
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2
  return EARTH_M * 2 * Math.atan2(Math.sqrt(Math.min(1, h)), Math.sqrt(1 - h))
}

export function polylineMeters(coords: Coord[]): number {
  let m = 0
  for (let i = 1; i < coords.length; i++) m += metersBetween(coords[i - 1], coords[i])
  return m
}

export function polylineMiles(coords: Coord[]): number {
  return polylineMeters(coords) / 1609.344
}

// Index of the polyline vertex nearest to pt.
export function nearestVertexIndex(coords: Coord[], pt: Coord): number {
  let best = 0
  let bestD = Infinity
  const px = pt[0] * Math.cos(toRad(pt[1]))
  const py = pt[1]
  for (let i = 0; i < coords.length; i++) {
    const x = coords[i][0] * Math.cos(toRad(coords[i][1]))
    const y = coords[i][1]
    const d = (x - px) ** 2 + (y - py) ** 2
    if (d < bestD) {
      bestD = d
      best = i
    }
  }
  return best
}

// Nearest point ON the polyline (not just its vertices): returns the snapped
// coordinate, the vertex index the projection lands in, and the fractional
// position along the whole line (0 = first point, 1 = last).
export function projectOnPolyline(
  pt: Coord,
  coords: Coord[],
): { pos: number; coord: Coord; index: number } {
  if (coords.length === 0) return { pos: 0, coord: pt, index: 0 }
  if (coords.length === 1) return { pos: 0, coord: coords[0], index: 0 }
  const px = pt[0] * Math.cos(toRad(pt[1]))
  const py = pt[1]
  const total = polylineMeters(coords)
  let cum = 0
  let best = { d: Infinity, pos: 0, coord: coords[0], index: 0 }
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1]
    const b = coords[i]
    const ax = a[0] * Math.cos(toRad(a[1]))
    const ay = a[1]
    const bx = b[0] * Math.cos(toRad(b[1]))
    const by = b[1]
    const abx = bx - ax
    const aby = by - ay
    const len2 = abx * abx + aby * aby
    let t = len2 > 0 ? ((px - ax) * abx + (py - ay) * aby) / len2 : 0
    t = Math.max(0, Math.min(1, t))
    const qx = ax + t * abx
    const qy = ay + t * aby
    const d = (qx - px) ** 2 + (qy - py) ** 2
    const segM = metersBetween(a, b)
    if (d < best.d) {
      best = {
        d,
        pos: total > 0 ? Math.min(1, (cum + t * segM) / total) : 0,
        coord: [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])],
        index: t < 0.5 ? i - 1 : i,
      }
    }
    cum += segM
  }
  return best
}

// Portion of the polyline EXCLUDED by a trim on the given end, as its own
// coordinate list — feeds the dashed overlay and the mileage readout.
export function excludedSegment(coords: Coord[], trim: RoadTrim, end: 'head' | 'tail'): Coord[] {
  const cut = end === 'head' ? trim.head : trim.tail
  if (!cut || coords.length < 2) return []
  const idx = nearestVertexIndex(coords, cut.coord)
  if (end === 'head') {
    if (idx <= 0) return []
    return [...coords.slice(0, idx + 1), cut.coord]
  }
  if (idx >= coords.length - 1) return []
  return [cut.coord, ...coords.slice(idx)]
}

// Sub-polyline between two fractional positions (0..1 along the length).
// Endpoints are interpolated so slices of adjacent ranges join seamlessly —
// this is what splits a trimmed road into ridden + excluded geometry.
export function polylineSlice(coords: Coord[], start: number, end: number): Coord[] {
  if (coords.length < 2) return coords.slice()
  const cum: number[] = [0]
  for (let i = 1; i < coords.length; i++) cum.push(cum[i - 1] + metersBetween(coords[i - 1], coords[i]))
  const total = cum[cum.length - 1]
  if (total <= 0) return coords.slice()
  const pointAt = (d: number): Coord => {
    for (let i = 1; i < coords.length; i++) {
      if (cum[i] >= d) {
        const seg = cum[i] - cum[i - 1]
        const t = seg > 0 ? (d - cum[i - 1]) / seg : 0
        return [
          coords[i - 1][0] + t * (coords[i][0] - coords[i - 1][0]),
          coords[i - 1][1] + t * (coords[i][1] - coords[i - 1][1]),
        ]
      }
    }
    return coords[coords.length - 1]
  }
  const s = Math.max(0, Math.min(1, start)) * total
  const e = Math.max(0, Math.min(1, end)) * total
  if (e - s < 1e-6) return [pointAt(s)]
  const out: Coord[] = [pointAt(s)]
  for (let i = 0; i < coords.length; i++) {
    if (cum[i] > s && cum[i] < e) out.push(coords[i])
  }
  out.push(pointAt(e))
  return out
}

export function trimActive(trim?: RoadTrim | null): boolean {
  return !!(trim?.head || trim?.tail || trim?.reversed)
}

// ── Duplicate passes ────────────────────────────────────────────────────────
// Trim keys identify the road INSTANCE (1st pass = plain id, 2nd = "id#2"…),
// so a road visited twice can carry two different trims. These helpers work
// on the whole route-trim map for one road id.
export function activeTrimsForRoad(routeTrims: RouteTrims, roadId: string): RoadTrim[] {
  const out: RoadTrim[] = []
  for (const [key, t] of Object.entries(routeTrims)) {
    if ((key === roadId || key.startsWith(`${roadId}#`)) && trimActive(t)) out.push(t)
  }
  return out
}

// Same lookup but INCLUDING empty trim objects: a visit seeded with {} is a
// full-length (untrimmed) pass, so the map counts it as riding the whole road.
export function allTrimsForRoad(routeTrims: RouteTrims, roadId: string): RoadTrim[] {
  const out: RoadTrim[] = []
  for (const [key, t] of Object.entries(routeTrims)) {
    if (key === roadId || key.startsWith(`${roadId}#`)) out.push(t)
  }
  return out
}

// Ridden ranges [start,end] (0..1 along the road) of all trims merged —
// overlapping passes fuse so shared sections render once, solid.
export function mergedRiddenRanges(trims: RoadTrim[]): [number, number][] {
  return mergeRanges(trims.map((t) => [t.head?.pos ?? 0, t.tail?.pos ?? 1] as [number, number]))
}

// Merge already-computed [start,end] ranges — same fusing as above, but takes
// raw ranges (e.g. the per-visit spans the map builds).
export function mergeRanges(ranges: [number, number][]): [number, number][] {
  const sorted = [...ranges].sort((a, b) => a[0] - b[0])
  const merged: [number, number][] = []
  for (const r of sorted) {
    const last = merged[merged.length - 1]
    if (last && r[0] <= last[1] + 0.0005) last[1] = Math.max(last[1], r[1])
    else merged.push([r[0], r[1]])
  }
  return merged
}

// How many times a road appears in a route id list.
export function visitCountOf(routeIds: string[], roadId: string): number {
  let n = 0
  for (const id of routeIds) if (id === roadId) n++
  return n
}

// Which part of a road the camera should frame when it's selected.
// visitKey picks a specific route visit (route-tray chip click): a trimmed
// visit frames its own ridden span, an untrimmed one the full road. With no
// visit key (map click) the view is the union of every visit's ridden span —
// the full road while ANY visit is untrimmed, since that visit rides it all.
export function selectionRanges(
  routeTrims: RouteTrims,
  routeIds: string[],
  roadId: string,
  visitKey?: string | null,
): [number, number][] | null {
  if (visitKey && (visitKey === roadId || visitKey.startsWith(`${roadId}#`))) {
    const t = routeTrims[visitKey]
    return t && trimActive(t) ? [[t.head?.pos ?? 0, t.tail?.pos ?? 1]] : [[0, 1]]
  }
  const active = activeTrimsForRoad(routeTrims, roadId)
  if (active.length === 0) return null
  if (visitCountOf(routeIds, roadId) > active.length) return [[0, 1]]
  return mergedRiddenRanges(active)
}

// Complement of the ridden ranges — the excluded sections to dash out.
export function complementRanges(ridden: [number, number][]): [number, number][] {
  const out: [number, number][] = []
  let cur = 0
  for (const [s, e] of ridden) {
    if (s - cur > 0.0005) out.push([cur, s])
    cur = Math.max(cur, e)
  }
  if (1 - cur > 0.0005) out.push([cur, 1])
  return out
}

// Junction snap candidates: where the road being trimmed passes within
// SNAP_METERS of another road in the route. For each other LineString road
// we take the closest pair of segments (one from each line) under the
// threshold and report its midpoint plus the other road's id. Subsamples
// every 2nd vertex to keep the O(n*m) sweep cheap.
export interface JunctionSnap {
  coord: Coord
  otherRoadId: string
}

export const SNAP_METERS = 25
const SNAP_VERTEX_STEP = 2

function segDistM(a: Coord, b: Coord, p: Coord, q: Coord): number {
  // Cheap upper bound: distance between the two segments' midpoints minus
  // half of each segment's length; if that's already over the threshold the
  // segments can't qualify, so the caller skips the expensive projection.
  const am: Coord = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
  const pm: Coord = [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]
  return metersBetween(am, pm)
}

// Project pt onto segment a-b, return the closest coordinate.
function projectOnSeg(pt: Coord, a: Coord, b: Coord): Coord {
  const px = pt[0] * Math.cos(toRad(pt[1]))
  const py = pt[1]
  const ax = a[0] * Math.cos(toRad(a[1]))
  const ay = a[1]
  const bx = b[0] * Math.cos(toRad(b[1]))
  const by = b[1]
  const abx = bx - ax
  const aby = by - ay
  const len2 = abx * abx + aby * aby
  let t = len2 > 0 ? ((px - ax) * abx + (py - ay) * aby) / len2 : 0
  t = Math.max(0, Math.min(1, t))
  return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]
}

export function junctionSnapCandidates(
  coords: Coord[],
  others: { roadId: string; coords: Coord[] }[],
): JunctionSnap[] {
  if (coords.length < 2) return []
  const out: JunctionSnap[] = []
  for (const other of others) {
    if (other.coords.length < 2) continue
    let bestDist = Infinity
    let bestCoord: Coord | null = null
    for (let i = 1; i < coords.length; i += SNAP_VERTEX_STEP) {
      const a = coords[i - 1]
      const b = coords[i]
      const midA: Coord = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
      for (let j = 1; j < other.coords.length; j += SNAP_VERTEX_STEP) {
        const p = other.coords[j - 1]
        const q = other.coords[j]
        // Bounding prefilter by midpoint distance before any projection.
        if (segDistM(a, b, p, q) - metersBetween(a, b) / 2 - metersBetween(p, q) / 2 > SNAP_METERS)
          continue
        const midB: Coord = [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2]
        const d = metersBetween(midA, midB)
        if (d < bestDist && d < SNAP_METERS) {
          bestDist = d
          bestCoord = projectOnSeg(midA, p, q)
        }
      }
    }
    if (bestCoord) out.push({ coord: bestCoord, otherRoadId: other.roadId })
  }
  return out
}

// Apply a trim to a road's waypoint chain: the cut coordinates replace the
// chain's endpoints so the connected route stops/starts exactly at the cut.
// Cuts are located by FRACTIONAL POSITION along the chain, never by nearest
// vertex: the sheet-link chain is coarse (often just start+end), so a cut
// anywhere past the midpoint projects nearest to the last vertex — the old
// nearest-vertex "nothing remains" guard then fired and silently returned
// the FULL road (visible only when the directionality guess didn't pre-flip
// the chain, i.e. exactly on explicitly reversed roads).
export function trimmedWaypoints(wps: Coord[], trim: RoadTrim): Coord[] {
  const head = trim.head?.coord ?? null
  const tail = trim.tail?.coord ?? null
  const hasCuts = !!(head || tail)
  if (!wps.length || (!hasCuts && !trim.reversed)) return wps
  if (!hasCuts || wps.length < 2) return trim.reversed ? [...wps].reverse() : wps

  // Fractional position of every vertex along the chain (0 = first, 1 = last).
  const total = polylineMeters(wps)
  const cum: number[] = [0]
  for (let i = 1; i < wps.length; i++) cum.push(cum[i - 1] + metersBetween(wps[i - 1], wps[i]))

  const headPos = head ? projectOnPolyline(head, wps).pos : 0
  const tailPos = tail ? projectOnPolyline(tail, wps).pos : 1
  const lo = Math.min(headPos, tailPos)
  const hi = Math.max(headPos, tailPos)

  let out: Coord[]
  if (hi - lo < 1e-6) {
    // Kept section is shorter than the coarse chain can represent — emit the
    // cut-to-cut span itself rather than falling back to the full road.
    out = head && tail ? [head, tail] : [head ?? (tail as Coord)]
  } else {
    out = [head ?? wps[0]]
    for (let i = 1; i < wps.length - 1; i++) {
      const p = total > 0 ? cum[i] / total : 0
      if (p > lo + 1e-6 && p < hi - 1e-6) out.push(wps[i])
    }
    out.push(tail ?? wps[wps.length - 1])
  }
  return trim.reversed ? [...out].reverse() : out
}
